<template>
  <div class="jps-upload">
    <label class="jps-upload">
      <p class="upload-file-message upload-file-title">{{ ls(message) }}</p>
      <div
        class="upload-draggable layout-col"
        :class="{
          'is-hovered': dragDropFocus,
        }"
        @dragover.prevent="updateDragDropFocus(true)"
        @dragleave.prevent="updateDragDropFocus(false)"
        @dragenter.prevent="updateDragDropFocus(true)"
        @drop.prevent="onFileChange"
      >
        <BaseIcon name="mdi/upload" class="upload-file-upload" />
        <p class="upload-file-message">{{ls({ ja: "ファイルの選択", en: "Select File" })}}</p>
      </div>
      <input
        type="file"
        ref="input"
        :multiple="multiple"
        :accept="accept"
        @change="onFileChange"
      />
    </label>
    <div class="upload-list" v-if="selectFileList.length > 0">
      <div class="upload-file" v-for="sf in selectFileList" :key="sf.id">
        <BaseImage
          v-if="sf.thumbnail"
          :src="sf.thumbnail"
          class="upload-file-thumbnail"
        />
        <span>{{ sf.name }}</span>
        <span v-if="sf.width">&nbsp;({{ sf.width }}x{{ sf.height }}px)</span>
        <span>({{ fileSize(sf) }}kb)</span>
        <UiButtonIcon
          type="button"
          class="upload-file-close"
          icon="mdi/close-circle"
          :arialabel="ls(dapLangGlobal.delete)"
          @click.prevent="deleteFile(sf)"
        />
      </div>
      <p v-if="multiple" class="select-message">
        {{
          lst(
            {
              ja: '{}件のファイルが選択されています。',
              en: '{} files selected.',
            },
            selectFileList.length,
          )
        }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'

interface SelectFile {
  id: string
  file: File
  name: string
  thumbnail: string
  width: number
  height: number
}

const { ls, lst } = useI18n()

interface Props {
  multiple?: boolean
  image?: boolean
  message?: LangString
  modelValue?: File[]
  accept?: string
}
const props = withDefaults(defineProps<Props>(), {
  multiple: false,
  image: false,
  message: () => ({ ja: "ファイルの選択", en: "Select File" }) as LangString
})

const emit = defineEmits(['update:modelValue'])

const dragDropFocus: Ref<boolean> = ref(false)
const selectFileList: Ref<SelectFile[]> = ref([])

const updateDragDropFocus = focus => {
  dragDropFocus.value = focus
}

watch(selectFileList, () => {
  emit(
    'update:modelValue',
    selectFileList.value.map(sf => sf.file),
  )
})

const fileSize = (file: SelectFile) => {
  return Math.ceil(file.file.size / 1024)
}

const ignoreOnChange = ref(false)

const { snackbarMsg } = useSnackbar()

const input = ref<HTMLInputElement>(null)

const onFileChange = async event => {
  if (ignoreOnChange.value) return
  const files: FileList = event.target.files || event.dataTransfer.files
  let newList: SelectFile[] = []
  if (props.multiple) newList.push(...selectFileList.value)
  for (let i = 0; i < files.length; i++) {
    let file: File = files.item(i)
    if (newList.some(s => s.name === file.name)) {
      snackbarMsg({ ja: `${file.name}は選択済みです。` })
      continue
    }
    let thumbnail = null
    let width = null
    let height = null
    if (props.image) {
      const createImage = await createThumbnail(
        files[i],
        100,
        100,
      )
      thumbnail = createImage.thumbnail
      width = createImage.width
      height = createImage.height
    }
    newList.push({
      id: luId(),
      file,
      name: file.name,
      thumbnail,
      width,
      height,
    })
    if (!props.multiple) break
  }
  selectFileList.value = newList
  dragDropFocus.value = false

  // event.target.files = null;
  ignoreOnChange.value = true
  input.value.value = ''
  ignoreOnChange.value = false
}

const deleteFile = file => {
  removeFromArray(selectFileList.value, file)
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-upload {
  position: relative;
  display: block;
  input[type='file'] {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    z-index: -1;
  }
  .upload-draggable {
    cursor: pointer;
    border-radius: 0.25rem;
    border-width: 1px;
    border-style: dashed;
    border-color: $jps-gray-300;
    text-align: center;
    align-items: center;
    padding: .25rem;
    &.is-disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    &.is-loading {
      position: relative;
      pointer-events: none;
      opacity: 0.5;
      &:after {
        /* // @include loader; */
        top: 0;
        left: calc(50% - 1.5em);
        width: 3em;
        height: 3em;
        border-width: 0.25em;
      }
    }
    &:hover,
    &.is-hovered {
      border-color: $jps-key-blue;
      background: rgba($jps-key-blue, 0.05);
    }
  }
  .upload-file {
    margin: 0.5rem 6px;
    display: flex;
    align-items: center;
    padding-bottom: 0.5rem;
    &:not(:last-child) {
      border-bottom: $basic-border;
    }
    > * {
      margin-right: 0.5rem;
    }
    > :last-child {
      margin-left: auto;
    }
  }
  .select-message {
    text-align: right;
    font-size: 0.875rem;
  }
  .upload-file-message,
  .upload-file-title {
    font-size: .875rem;
    font-weight: 400;
  }

  .upload-file-upload {
      width: 2rem;
      height: 2rem;
      margin-top: 9px;
      margin-bottom: 9px;
      color: $black;
  }
  .upload-file-thumbnail {
      width: 4rem;
      height: 4rem;
      object-fit: contain;
  }
  .upload-file-close {
      width: 1.5rem;
      height: 1.5rem;
      color: $jps-gray-600;
  }
}
</style>
